import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import AdminGallery from "../components/AdminGallery";

const STATUS_COLORS = {
  pending: "bg-[#E8F0F3] text-[#4A6178]",
  confirmed: "bg-[#B5CDD8]/40 text-[#1A3A52]",
  rejected: "bg-[#6AB7A8]/20 text-[#2c5b52]",
};

const STATUS_LABEL = { pending: "Pendiente", confirmed: "Confirmada", rejected: "Rechazada" };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("bookings");

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
        await load();
      } catch {
        nav("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    const r = await api.get("/bookings");
    setBookings(r.data);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Cita ${STATUS_LABEL[status].toLowerCase()}`);
      load();
    } catch { toast.error("Error al actualizar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cita?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Cita eliminada");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    nav("/admin/login");
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = {
    all: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    rejected: bookings.filter((b) => b.status === "rejected").length,
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-[color:var(--text-secondary)]">Cargando…</div>;

  return (
    <div className="min-h-screen bg-[color:var(--bg-primary)]">
      <header className="border-b border-[color:var(--border-subtle)] glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-2xl">Dentainhoa<span className="text-[color:var(--accent)]">.</span> <span className="text-xs font-mono uppercase tracking-widest text-[color:var(--text-secondary)] ml-2">/ admin</span></Link>
          <div className="flex items-center gap-4">
            <span className="text-xs font-mono text-[color:var(--text-secondary)] hidden md:inline">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="text-xs uppercase tracking-widest hover:text-[color:var(--accent)]">Salir</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--text-secondary)] mb-4">Panel</div>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tighter">{tab === "bookings" ? "Reservas" : "Galería"}</h1>
        </div>

        <div className="flex gap-2 mb-8 border-b border-[color:var(--border-subtle)]">
          {[
            { k: "bookings", label: "Reservas" },
            { k: "gallery", label: "Galería" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-5 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === t.k ? "border-[color:var(--text-primary)] text-[color:var(--text-primary)]" : "border-transparent text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "gallery" ? (
          <AdminGallery />
        ) : (
        <>
        <div className="flex flex-wrap gap-2 mb-8">
          {["all", "pending", "confirmed", "rejected"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`rounded-full px-5 py-2 text-sm border transition-colors ${filter === k ? "bg-[color:var(--text-primary)] text-[color:var(--bg-primary)] border-[color:var(--text-primary)]" : "border-[color:var(--border-subtle)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"}`}
            >
              {k === "all" ? "Todas" : STATUS_LABEL[k]} · {counts[k]}
            </button>
          ))}
        </div>

        <div className="border border-[color:var(--border-subtle)] rounded-2xl overflow-hidden bg-white/40">
          {filtered.length === 0 ? (
            <div className="p-16 text-center text-[color:var(--text-secondary)]" data-testid="empty-bookings">Sin reservas por ahora.</div>
          ) : (
            <ul className="divide-y divide-[color:var(--border-subtle)]">
              {filtered.map((b) => (
                <li key={b.id} className="p-6 md:p-8 grid grid-cols-12 gap-4 items-start" data-testid={`booking-row-${b.id}`}>
                  <div className="col-span-12 md:col-span-4">
                    <div className="font-heading text-2xl tracking-tight">{b.name}</div>
                    <div className="text-sm text-[color:var(--text-secondary)] mt-1">{b.email} · {b.phone}</div>
                    <div className="text-sm text-[color:var(--text-secondary)]">{b.address}</div>
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-1">Servicio</div>
                    <div>{b.service}</div>
                    {b.notes && <div className="text-sm text-[color:var(--text-secondary)] mt-2 italic">&ldquo;{b.notes}&rdquo;</div>}
                  </div>
                  <div className="col-span-6 md:col-span-2 font-mono text-sm">
                    <div>{b.date}</div>
                    <div className="text-[color:var(--text-secondary)]">{b.time}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3 flex flex-col md:items-end gap-3">
                    <span className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full ${STATUS_COLORS[b.status]}`}>{STATUS_LABEL[b.status]}</span>
                    {b.deposit_paid && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[color:var(--accent)]/20 text-[color:var(--accent-hover)]" data-testid={`deposit-paid-${b.id}`}>Depósito pagado</span>
                    )}
                    <div className="flex gap-2">
                      {b.status !== "confirmed" && (
                        <button onClick={() => setStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`} className="text-xs px-3 py-1.5 rounded-full bg-[color:var(--signature-dark)] text-white hover:opacity-90">Confirmar</button>
                      )}
                      {b.status !== "rejected" && (
                        <button onClick={() => setStatus(b.id, "rejected")} data-testid={`reject-${b.id}`} className="text-xs px-3 py-1.5 rounded-full border border-[color:var(--border-strong)] hover:bg-[color:var(--text-primary)] hover:text-[color:var(--bg-primary)]">Rechazar</button>
                      )}
                      <button onClick={() => remove(b.id)} data-testid={`delete-${b.id}`} className="text-xs px-3 py-1.5 rounded-full text-[color:var(--text-secondary)] hover:text-[#c0392b]">Eliminar</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
